import { ImageOff } from 'lucide-react';
import { useState } from 'react';

import {
  EmptyState,
  FormActions,
  LoadError,
  Modal,
  HasMorePagination,
  SearchBar,
  Skeleton,
} from '~/components/ui';
import { useDebouncedQuery } from '~/hooks/useDebouncedQuery';
import { queryErrorMessage } from '~/modules/query/base';
import { useListDesignTemplatesQuery } from '~/modules/query/notification/designTemplates';
import type { DesignTemplate } from '~/modules/query/notification/types';
import { designName } from '~/lib/notification/designTemplates';

interface DesignPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The design already linked, shown selected. */
  currentId: string;
  onAssociate: (design: DesignTemplate) => void;
}

/** The thumbnail, or a placeholder while service-design has not rendered one. */
export function DesignThumbnail({
  design,
  className,
}: {
  design: DesignTemplate;
  className: string;
}) {
  const src = design.thumbnailUrl;
  const [failed, setFailed] = useState<string | null>(null);
  if (!src || failed === src) {
    return (
      <div className={`flex items-center justify-center bg-gray-50 dark:bg-white/5 ${className}`}>
        <ImageOff className="size-6 text-gray-400 dark:text-gray-500" aria-label="No preview yet" />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={`${designName(design)} preview`}
      className={`bg-white object-cover object-top ${className}`}
      onError={() => setFailed(src)}
    />
  );
}

/**
 * Picks a service-design email design (listed through service-notification) to link to the email channel (ui-design's email
 * grid: thumbnail and name). Search and page are the modal's own state: it feeds an unsaved
 * draft, so a reload has nothing to reopen it on.
 */
export default function DesignPickerModal({
  isOpen,
  onClose,
  currentId,
  onAssociate,
}: DesignPickerModalProps) {
  const [search, setSearch] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const [selected, setSelected] = useState<DesignTemplate | null>(null);
  const settled = useDebouncedQuery(search);
  const { data, error, isLoading, isFetching, refetch } = useListDesignTemplatesQuery(
    { name: settled, pageIndex },
    { skip: !isOpen },
  );
  const selectedId = selected?.id ?? currentId;
  const unchanged = !selected || selected.id === currentId;

  const close = () => {
    setSelected(null);
    onClose();
  };
  const associate = () => {
    if (!selected) return;
    onAssociate(selected);
    close();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      size="xlarge"
      title="Choose an email design"
      description="Email designs from the Back At You design library. The linked design replaces the email body."
      testId="design-picker"
      footer={
        <FormActions
          onDiscard={close}
          onSave={associate}
          saveLabel="Associate"
          disabled={unchanged}
          disabledReason={selected ? 'This design is already linked' : 'Pick a design'}
          saveTestId="design-picker-associate"
        />
      }
    >
      <div className="space-y-4">
        <SearchBar
          value={search}
          onChange={(value) => {
            setSearch(value);
            setPageIndex(0);
          }}
          placeholder="Search designs by name…"
          testId="design-picker-search"
        />
        {error ? (
          <LoadError
            what="designs"
            message={queryErrorMessage(error) ?? 'The design library did not answer. Try again.'}
            onRetry={() => void refetch()}
          />
        ) : isLoading ? (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {Array.from({ length: 8 }, (_, i) => (
              <Skeleton key={i} variant="block" className="rounded-xl" />
            ))}
          </div>
        ) : !data?.items.length ? (
          <EmptyState
            title={settled ? 'No designs match' : 'No email designs yet'}
            description={
              settled
                ? 'Try another name.'
                : 'Create an email design in the design library, then pick it here.'
            }
          />
        ) : (
          <ul
            className={`grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 ${isFetching ? 'opacity-60' : ''}`}
            data-testid="design-picker-grid"
          >
            {data.items.map((design) => {
              const isSelected = design.id === selectedId;
              return (
                <li key={design.id}>
                  <button
                    type="button"
                    aria-pressed={isSelected}
                    onClick={() => setSelected(design)}
                    data-testid={`design-option-${design.id}`}
                    className={`w-full overflow-hidden rounded-xl border text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${
                      isSelected
                        ? 'border-brand-500 ring-2 ring-brand-500'
                        : 'border-gray-200 hover:border-brand-300 dark:border-gray-800'
                    }`}
                  >
                    <DesignThumbnail design={design} className="h-40 w-full" />
                    <span className="block border-t border-gray-200 px-3 py-2 dark:border-gray-800">
                      <span className="block truncate text-sm font-medium text-gray-800 dark:text-white/90">
                        {designName(design)}
                      </span>
                      {design.id === currentId && (
                        <span className="block text-xs text-gray-500 dark:text-gray-400">
                          Linked now
                        </span>
                      )}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        {data && (
          <HasMorePagination
            pageIndex={pageIndex}
            hasMore={data.hasMore}
            onPageChange={setPageIndex}
          />
        )}
      </div>
    </Modal>
  );
}
